/**
 * Redux reducer for audio playback state, managing playlists and playback controls.
 *
 * @module redux/audio/audioReducer
 * @author Snowfly
 * @version 1.0.0
 */
import {
    RESET_LIST,
    SET_LIST,
    SET_LIST_TYPE,
    CLEAR_LIST,
    PLAY_LIST,
    PAUSE_LIST,
    STOP_LIST,
    PLAY_AUDIO,
    PAUSE_AUDIO,
    STOP_AUDIO,
} from './audioTypes';

/**
 * Initial state for the audio reducer.
 *
 * @type {Object}
 * @property {Array} scripts - List of audio script objects in the current playlist.
 * @property {Array} indexes - Ordered list of script IDs used for index lookups.
 * @property {number} currentIndex - Index of the currently active audio item; -1 when idle.
 * @property {boolean} playing - Whether audio is currently playing.
 * @property {string} type - Playback mode: 'single' for one item or 'list' for sequential.
 */
let initialState = {
    scripts: [],
    indexes: [],
    currentIndex: -1,
    playing: false,
    type: 'single',
};

/**
 * Audio reducer function.
 *
 * @param {Object} state - Current audio state.
 * @param {Object} action - Dispatched Redux action.
 * @returns {Object} The next audio state.
 */
const audioReducer = (state = initialState, action) => {
    const { payload, type } = action;
    switch (type) {
        case RESET_LIST:
        case CLEAR_LIST:
            return initialState;

        case SET_LIST:
            return {
                ...state,
                scripts: payload.scripts,
                indexes: payload.scripts.map(item => item._id),
                currentIndex: payload.currentIndex ?? -1,
                playing: false,
                type: payload.type ?? 'single',
            };

        case SET_LIST_TYPE:
            return {
                ...state,
                type: payload ?? 'single',
            };

        case PLAY_LIST:
            return {
                ...state,
                currentIndex: payload,
                playing: true,
            };

        case PAUSE_LIST:
            return {
                ...state,
                playing: false,
            };

        case STOP_LIST:
            return {
                ...state,
                currentIndex: -1,
                playing: false,
                type: '',
            };

        case PLAY_AUDIO:
            return {
                ...state,
                currentIndex: state.indexes.indexOf(payload),
                playing: true,
                type: 'single',
            };

        case PAUSE_AUDIO:
            return {
                ...state,
                currentIndex: state.indexes.indexOf(payload),
                playing: false
            };

        case STOP_AUDIO:
            return {
                ...state,
                currentIndex: state.indexes.indexOf(payload),
                playing: false
            };

        default:
            return state;
    }
}

export default audioReducer;